import { useEffect, useMemo, useRef } from 'preact/hooks'
import PlpCard from 'snippets/plp-card/plp-card'
import { SkeletonLine, SkeletonProduct } from 'snippets/skeleton/skeleton'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import { getProductTitle } from 'libs/shopify'
import { getTags, getPrice, getVariants } from 'libs/searchspring/searchspring-utils'
import Image from 'snippets/image/image'

const selectors = {
  MAX_SUGGESTION: 4
}

function SearchDropdown({ state, closeSearch }) {
  const isLoading = useMemo(() => state.isLoading.value, [state.isLoading.value])
  const isOpenDropdown = useMemo(() => state.isOpenDropdown.value, [state.isOpenDropdown.value])
  const isTrendingActive = useMemo(() => state.isTrendingActive.value, [state.isTrendingActive.value])
  const correctedQuery = useMemo(() => state.correctedQuery.value, [state.correctedQuery.value])
  const searchValue = useMemo(() => state.searchValue.value, [state.searchValue.value])
  const searchTrendingValue = useMemo(() => state.searchTrendingValue.value, [state.searchTrendingValue.value])
  const suggestions = useMemo(() => state.suggestions.value, [state.suggestions.value])
  const results = useMemo(() => state.results.value, [state.results.value])
  const { labelSuggestions, textNoResult } = GM_STATE?.integrations?.searchSpringConfig || {}
  const searchDropdownRef = useRef()
  const isScrollLockedRef = useRef(false)

  useEffect(() => {
    const dropdown = searchDropdownRef.current
    if (!dropdown) return

    if (isOpenDropdown && !isScrollLockedRef.current) {
      disableBodyScroll(dropdown)
      isScrollLockedRef.current = true
    }

    if (!isOpenDropdown && isScrollLockedRef.current) {
      clearAllBodyScrollLocks()
      isScrollLockedRef.current = false
    }

    return () => {
      if (isScrollLockedRef.current) {
        clearAllBodyScrollLocks()
        isScrollLockedRef.current = false
      }
    }
  }, [isOpenDropdown])

  const searchTrendingConfig = window.searchTrendingConfig

  useEffect(() => {
    if (!isOpenDropdown) return
    if (!results || results.length === 0) return
    // Ensure DOM is painted before triggering Swym to (re)initialize wishlist buttons in the dropdown only
    const id = setTimeout(() => {
      if (window._swat && window._swat.initializeActionButtons) {
        window._swat.initializeActionButtons('.js-search-dropdown')
      }
    }, 0)
    return () => clearTimeout(id)
  }, [isOpenDropdown, results])

  return (
    <>
      <div
        ref={searchDropdownRef}
        className="container scrollbar fixed pt-4 pb-6 flex flex-col left-0 top-[calc(var(--topbar-height)+120px)] z-20 w-full max-h-[calc(100dvh-var(--topbar-height)-120px)] bg-white overflow-y-auto border-t lg:flex-row lg:py-12 lg:top-[calc(var(--topbar-height)+80px)] lg-max:[.is-pin_&]:max-h-[calc(100dvh-var(--topbar-height)-80px)] lg:max-h-[564px] 3xl:max-h-[30vw] js-search-dropdown"
      >
        {isLoading || (correctedQuery && correctedQuery !== searchValue) || (suggestions && suggestions.length > 0) || (searchTrendingValue && searchTrendingValue.length > 0) ? (
          <div className="pb-6 lg:w-[336px] lg:pr-8 lg:pb-0">
            <div className="text-left">
              {(((suggestions && suggestions.length > 0) || (searchTrendingValue && searchTrendingValue.length > 0 && isTrendingActive)) && !isLoading) && <h3 className="mb-2 text-sm font-medium text-grey-1000">{labelSuggestions}</h3>}
              {isLoading && <div className="w-28">{SkeletonLine(selectors.MAX_SUGGESTION)}</div>}
              {suggestions && suggestions.length > 0 && !isTrendingActive && !isLoading ? (
                <div className="flex flex-col gap-2">
                  {suggestions.map(
                    (item) =>
                      <a className="text-sm no-underline w-fit text-grey-700 hover:text-secondary-hover" href={`/search?q=${item.text}`} title={item.text}>
                        {item.text}
                      </a>
                  )}
                </div>
              ) : null}
              {searchTrendingValue && searchTrendingValue.length > 0 && isTrendingActive && !isLoading ? (
                <div className="flex flex-col gap-2">
                  {searchTrendingValue.map(
                    (item) =>
                      <a className="w-fit text-sm text-grey-700 no-underline hover:text-secondary-hover" href={`/search?q=${item.searchQuery}`} title={item.searchQuery}>
                        {item.searchQuery}
                      </a>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="lg:flex-1 lg:h-full">
          {isLoading ? (
            <div className="grid grid-cols-2 gap-x-4 gap-y-6 lg:grid-cols-4 lg:gap-6">{SkeletonProduct(4)}</div>
          ) : (
            <div className="relative">
              {!isTrendingActive ? (
                results && results.length ? (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-6 lg:grid-cols-4 lg:gap-x-6 lg:gap-y-8">
                    {results.map((product) => (
                      <PlpCard
                        title={getProductTitle(product.name)}
                        subtitle={product.mfield_product_details_size}
                        tags={getTags(product.tags)}
                        handle={product.handle}
                        imageUrl={[product.imageUrl]}
                        price={getPrice(product.price)}
                        originalPrice={getPrice(product.msrp)}
                        className="flex flex-col h-full plp-card-search"
                        ratioImage="aspect-1"
                        isShowBadge
                        linkedColors={product.mfield_linked_products_colour}
                        currentSize={product.mfield_variant_options_size}
                        sku={product.sku}
                        showCallout={false}
                        variants={getVariants(product.variants)}
                        initVariant={getVariants(product.variants)?.[0]}
                      />
                    ))}
                  </div>
                ) : (
                  <div>{textNoResult}</div>
                )
              ) : (
                searchTrendingConfig && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-6 lg:grid-cols-4 lg:gap-x-6 lg:gap-y-8">
                  {searchTrendingConfig.map(card => (
                    <a href={card.link} className='group' title={card.title}>
                      <Image
                        imageUrl={[card.image]}
                        alt={card.title}
                        className="w-full aspect-[164/120] lg:aspect-[234/172] overflow-hidden"
                        imageClass="w-full h-full hover:zoom transition"
                        imageBreakpoints="(min-width: 768px)|(min-width: 0px)"
                        isCover={true}
                        isLazy={true}
                      />
                      {card.title && <h3 className="mt-2 text-sm text-grey-1000 group-hover:underline group-hover:text-link-hover lg:mt-4">{card.title}</h3>}
                    </a>
                  ))}
                </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
      <div
        className="fixed z-0 top-[calc(var(--topbar-height)+120px)] left-0 h-screen w-full bg-dark-overlay lg:top-[calc(var(--topbar-height)+80px)]"
        onClick={closeSearch}
      ></div>
    </>
  )
}

export default SearchDropdown
